import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Sentinel } from "../../model_gen/sentinel";
import { SentinelService } from "../../services/SentinelService";
import { SeverityLevel } from "../../types/ISentinels";
import { Button } from "../ui/button";
import { EditIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";

interface SentinelsTableProps {
  intentGroupId: number;
  emptyText?: React.ReactNode;
}

const SentinelsTable: React.FC<SentinelsTableProps> = ({
  intentGroupId,
  emptyText,
}) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  
  const [sentinels, setSentinels] = useState<Sentinel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig<Sentinel>>({ key: null, direction: "desc" });
  const pageSize = 10;

  const severityColorMap: Record<SeverityLevel, string> = {
    [SeverityLevel.UNRECOGNIZED]: "bg-muted text-foreground",
    [SeverityLevel.INFO]: "bg-muted text-foreground",
    [SeverityLevel.WARN]: "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
    [SeverityLevel.ERROR]: "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
    [SeverityLevel.FATAL]: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
  };

  const getSentinels = useCallback(() => {
    if (intentGroupId) {
      SentinelService.getSentinelsByIntentGroup(
        intentGroupId,
        setIsLoading,
        apiKey,
        currentPage,
        pageSize
      ).subscribe(
        (response: { data: Sentinel[]; total: number }) => {
          setSentinels(response.data);
          setTotal(response.total);
          setIsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    }
  }, [intentGroupId, apiKey, currentPage, pageSize]);

  useEffect(() => {
    getSentinels();
  }, [getSentinels]);

  const handleSort = (key: keyof Sentinel) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<Sentinel>[] = [
    {
      key: "sentinel_name",
      header: "Name",
      width: "w-[30%]",
      sortable: true,
      render: (sentinel) => (
        <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
          {sentinel.sentinel_name}
        </div>
      )
    },
    {
      key: "state",
      header: "State",
      width: "w-[15%]",
      sortable: true,
      render: (sentinel) => (
        <div className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          sentinel.state === "ACTIVE" 
            ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
            : sentinel.state === "DISABLED"
            ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
            : "bg-muted text-foreground"
        )}>
          {sentinel.state}
        </div>
      )
    },
    {
      key: "severity",
      header: "Severity",
      width: "w-[15%]",
      sortable: true,
      render: (sentinel) => (
        <div className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          severityColorMap[sentinel.severity as SeverityLevel]
        )}>
          {SeverityLevel[sentinel.severity as SeverityLevel]}
        </div>
      )
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[25%]",
      sortable: true,
      render: (sentinel) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(sentinel.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(sentinel.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "id",
      header: "Actions",
      width: "w-[15%]",
      render: (sentinel) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/intent-group/${intentGroupId}/sentinel/${sentinel.id}`);
          }}
        >
          <EditIcon className="w-3 h-3" />
          Edit
        </Button>
      )
    }
  ];

  const handleRowClick = (sentinel: Sentinel) => {
    navigate(`/intent-group/${intentGroupId}/sentinel/${sentinel.id}`);
  };

  const defaultSort = (a: Sentinel, b: Sentinel) => {
    // Default sort by severity descending
    return b.severity - a.severity;
  };

  return (
    <PaginatedTable
      data={sentinels}
      columns={columns}
      loading={isLoading}
      sortConfig={sortConfig}
      onSort={handleSort}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      totalItems={total}
      emptyTitle="No sentinels found"
      emptyDescription="Create a new sentinel to monitor and evaluate intents."
      onRowClick={handleRowClick}
      defaultSort={defaultSort}
      serverSidePagination={true}
    />
  );
};

export default SentinelsTable; 